import { useMutation } from '@apollo/client';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import { ADD_DOMAIN } from '../../../../helpers/graphql/hosting';
import TextField from '@mui/material/TextField';
import isFQDN from 'validator/lib/isFQDN';
import { useSnackbar } from 'notistack';
import DnsTable from './DnsTable';

export default function AddDomainWizard({
  blogId,
  setBuildProgress,
  setStatus,
  startPolling,
  open,
  setOpen,
}) {
  const [addDomain] = useMutation(ADD_DOMAIN);
  const [loading, setLoading] = useState(false);

  const [domain, setDomain] = useState('');
  const [isNextStep, setIsNextStep] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [availability, setAvailability] = useState(null);

  const checkDomainAvailability = async domainName => {
    try {
      const response = await fetch(
        `/api/domain-availability?domain=${domainName}`,
      );
      const data = await response.json();
      setAvailability(data.isAvailable);
    } catch (error) {
      console.error('Error checking domain availability:', error);
    }
  };

  function checkDomain() {
    if (isFQDN(domain)) {
      checkDomainAvailability(domain);
      setIsNextStep(true);
    } else
      enqueueSnackbar(
        'Domain is not valid. Enter your domain like www.example.com or example.com (no http:// or https:// or example.com/ etc.)',
        { variant: 'error' },
      );
  }

  const handleAddDomain = () => {
    setLoading(true);
    addDomain({
      variables: {
        blogId,
        domain,
      },
    })
      .then(({ data }) => {
        if (data?.addDomain?.success) {
          setOpen(false);
          setBuildProgress(5);
          setStatus('loading');
          startPolling(5000);
          window.location.href = `/dashboard/hosting/general?blogId=${blogId}`;
        } else
          enqueueSnackbar('Your domain could not be added', {
            variant: 'error',
          });
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar('Something went wrong', { variant: 'error' });
        setLoading(false);
      });
  };

  return (
    <>
      <Dialog
        open={open}
        aria-labelledby="alert-domainwizard-title"
        aria-describedby="alert-domainwizard-description"
      >
        <DialogTitle id="alert-domainwizard-title">Add domain</DialogTitle>
        <DialogContent>
          {isNextStep ? (
            <>
              {availability && (
                <DialogContentText>
                  To add a domain, you first need to buy it from a domain
                  registar. We recommend{' '}
                  <a
                    href={`https://www.tkqlhce.com/click-100956265-15083042?url=${encodeURIComponent(
                      `https://www.namecheap.com/domains/registration/results`,
                    )}`}
                    target="_blank"
                    rel="nofollow noreferrer noopener"
                    className="tw-text-green-600 hover:tw-text-green-500 tw-underline hover:tw-underline"
                  >
                    Namecheap
                  </a>
                  .
                </DialogContentText>
              )}
              <DialogContentText>
                Set these DNS records at your domain registar (
                <a
                  href={`/help/article/how-to-add-your-own-domain#step-2-modify-your-dns-records`}
                  target="_blank"
                  rel="nofollow noreferrer noopener"
                  className="tw-text-green-600 hover:tw-text-green-500 tw-underline hover:tw-underline"
                >
                  How to do this?
                </a>
                ):
              </DialogContentText>
              <div className="tw-py-4">
                <DnsTable blogId={blogId} />
              </div>

              <DialogContentText>
                <p>
                  Unable to set ALIAS or CNAME record for @?{' '}
                  <a
                    href={`/help/article/how-to-add-your-own-domain#alternatives-for-registrars-that-don-t-support-alias-or-cname-record-for`}
                    target="_blank"
                    rel="nofollow noreferrer noopener"
                    className="tw-text-green-600 hover:tw-text-green-500 tw-underline hover:tw-underline"
                  >
                    See your options here
                  </a>
                  .
                </p>
                <p>
                  It can take up to 24 hours for your blog to show globally on
                  your new domain due to the way DNS works. Finally, don&apos;t
                  forget to click <i>Save changes</i> below.
                </p>
              </DialogContentText>
            </>
          ) : (
            <>
              <DialogContentText>
                Enter your domain either as www.example.com (recommended) or
                example.com
              </DialogContentText>
              <TextField
                className="tw-mt-3"
                autoFocus
                value={domain}
                onChange={e => setDomain(e.target.value)}
                label="Your domain"
                placeholder="www.example.com"
                variant="outlined"
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(undefined)} color="primary">
            Cancel
          </Button>
          {isNextStep ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddDomain}
              disabled={loading}
            >
              Save Changes
            </Button>
          ) : (
            <Button variant="contained" color="primary" onClick={checkDomain}>
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
